import {assertAndContinue} from 'Asserts';
import SpreadsheetBulkImportStore from 'bulk-import/SpreadsheetBulkImportStore';
// @ts-expect-error
import BulkAntibodyStore from 'bulk-workflows/antibody/BulkAntibodyStore';
import BulkAssemblyStore from 'bulk-workflows/assembly/BulkAssemblyStore';
import BulkTranslationStore from 'bulk-workflows/translation/BulkTranslationStore';
import CalendarStore from 'calendar/CalendarStore';
// @ts-expect-error
import FileBrowserStore from 'file-browser/FileBrowserStore';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import InventoryFileStore from 'inventory/InventoryFileStore';
import Store from 'lib/Store';
import CRISPRAssemblyStore from 'molbio/crispr/assembly/CRISPRAssemblyStore';
import CRISPRAnalysisStore from 'molbio/crispr/CRISPRAnalysisStore';
import HRTemplateStore from 'molbio/crispr/hr-template/HRTemplateStore';
import OrganismStore from 'molbio/genome/organism/OrganismStore';
import ProteinAlignmentStore from 'molbio/proteins/ProteinAlignmentStore';
import ProteinStore from 'molbio/proteins/ProteinStore';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import NavStore from 'nav/NavStore';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import ToastStore from 'nav/toasts/ToastStore';
import NoteStore from 'notebook/bioentities/NoteStore';
import DataTableConfigStore from 'notebook/data-tables/DataTableConfigStore';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
// @ts-expect-error
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import EditorStore from 'notebook/protocols/stores/EditorStore';
import TimerStore from 'notebook/protocols/timers/TimerStore';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import TFIStore from 'notebook/tfi/TFIStore';
// @ts-expect-error
import NewUserFlowStore from 'onboarding/new-user-flow/NewUserFlowStore';
import RegistryActivationFlowStore from 'onboarding/registry-activation-flow/RegistryActivationFlowStore';
// @ts-expect-error
import PartyPageStore from 'orgs/PartyPageStore';
import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore, Thunk} from 'redux-stores/Store';
import RegistryStore from 'registry/RegistryStore';
import RegistryValidatorStore from 'registry/RegistryValidatorStore';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import AssayStore from 'results/AssayStore';
// @ts-expect-error
import ApplicableBatchesStore from 'samples/ApplicableBatchesStore';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import SampleStore from 'samples/SampleStore';
import StorableStore from 'samples/StorableStore';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import EntityStore from 'stores/EntityStore';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import GenomeStore from 'stores/GenomeStore';
import WorkflowEntryTemplateStore from 'workflows/entry-templates/WorkflowEntryTemplateStore';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';

export type Unsubscribe = () => void;

export default class StoreWrapper {
  // Subclasses should override this to indicate which stores to subscribe to.
  readonly stores: Array<Store> = [];
  readonly shouldSubscribeToRedux: boolean = true;

  reduxState(): BenchlingState {
    assertAndContinue(
      this.shouldSubscribeToRedux,
      'Tried to access redux state without subscribing to redux.'
    );
    return getStore().getState();
  }

  dispatch<T = void>(action: BenchlingAction | Thunk<T>): T {
    return getStore().dispatch(action);
  }

  applicableBatchesStore(): typeof ApplicableBatchesStore {
    return this.requiredStore(ApplicableBatchesStore);
  }

  assayStore(): typeof AssayStore {
    return this.requiredStore(AssayStore);
  }

  bulkAntibodyStore(): typeof BulkAntibodyStore {
    return this.requiredStore(BulkAntibodyStore);
  }

  bulkAssemblyStore(): typeof BulkAssemblyStore {
    return this.requiredStore(BulkAssemblyStore);
  }

  bulkTranslationStore(): typeof BulkTranslationStore {
    return this.requiredStore(BulkTranslationStore);
  }

  calendarStore(): typeof CalendarStore {
    return this.requiredStore(CalendarStore);
  }

  crisprAnalysisStore(): typeof CRISPRAnalysisStore {
    return this.requiredStore(CRISPRAnalysisStore);
  }

  crisprAssemblyStore(): typeof CRISPRAssemblyStore {
    return this.requiredStore(CRISPRAssemblyStore);
  }

  dataTableConfigStore(): typeof DataTableConfigStore {
    return this.requiredStore(DataTableConfigStore);
  }

  editorStore(): typeof EditorStore {
    return this.requiredStore(EditorStore);
  }

  entityStore(): typeof EntityStore {
    return this.requiredStore(EntityStore);
  }

  entryStore(): typeof EntryStore {
    return this.requiredStore(EntryStore);
  }

  fileBrowserStore(): typeof FileBrowserStore {
    return this.requiredStore(FileBrowserStore);
  }

  fullScreenStore(): typeof FullScreenStore {
    return this.requiredStore(FullScreenStore);
  }

  genomeStore(): typeof GenomeStore {
    return this.requiredStore(GenomeStore);
  }

  homeDashboardStore(): typeof HomeDashboardStore {
    return this.requiredStore(HomeDashboardStore);
  }

  hrTemplateStore(): typeof HRTemplateStore {
    return this.requiredStore(HRTemplateStore);
  }

  inventoryFileStore(): typeof InventoryFileStore {
    return this.requiredStore(InventoryFileStore);
  }

  organismStore(): typeof OrganismStore {
    return this.requiredStore(OrganismStore);
  }

  partyPageStore(): typeof PartyPageStore {
    return this.requiredStore(PartyPageStore);
  }

  navStore(): typeof NavStore {
    return this.requiredStore(NavStore);
  }

  newUserFlowStore(): typeof NewUserFlowStore {
    return this.requiredStore(NewUserFlowStore);
  }

  noteStore(): typeof NoteStore {
    return this.requiredStore(NoteStore);
  }

  proteinAlignmentStore(): typeof ProteinAlignmentStore {
    return this.requiredStore(ProteinAlignmentStore);
  }

  proteinStore(): typeof ProteinStore {
    return this.requiredStore(ProteinStore);
  }

  registryActivationFlowStore(): typeof RegistryActivationFlowStore {
    return this.requiredStore(RegistryActivationFlowStore);
  }

  registryStore(): typeof RegistryStore {
    return this.requiredStore(RegistryStore);
  }

  registryValidatorStore(): typeof RegistryValidatorStore {
    return this.requiredStore(RegistryValidatorStore);
  }

  schemaFieldSelectorStore(): typeof SchemaFieldSelectorStore {
    return this.requiredStore(SchemaFieldSelectorStore);
  }

  sampleSchemaStore(): typeof SampleSchemaStore {
    return this.requiredStore(SampleSchemaStore);
  }

  sampleStore(): typeof SampleStore {
    return this.requiredStore(SampleStore);
  }

  sidepanelStore(): typeof SidepanelStore {
    return this.requiredStore(SidepanelStore);
  }

  spreadsheetBulkImportStore(): typeof SpreadsheetBulkImportStore {
    return this.requiredStore(SpreadsheetBulkImportStore);
  }

  spreadsheetStore(): typeof SpreadsheetStore {
    return this.requiredStore(SpreadsheetStore);
  }

  stageEntryStore(): typeof StageEntryStore {
    return this.requiredStore(StageEntryStore);
  }

  storableStore(): typeof StorableStore {
    return this.requiredStore(StorableStore);
  }

  subtemplateStore(): typeof SubtemplateStore {
    return this.requiredStore(SubtemplateStore);
  }

  templateStore(): typeof TemplateStore {
    return this.requiredStore(TemplateStore);
  }

  tfiStore(): typeof TFIStore {
    return this.requiredStore(TFIStore);
  }

  timerStore(): typeof TimerStore {
    return this.requiredStore(TimerStore);
  }

  toastStore(): typeof ToastStore {
    return this.requiredStore(ToastStore);
  }

  workflowEntryTemplateStore(): typeof WorkflowEntryTemplateStore {
    return this.requiredStore(WorkflowEntryTemplateStore);
  }

  workspaceStore(): typeof WorkspaceStore {
    return this.requiredStore(WorkspaceStore);
  }

  private requiredStore<T extends Store>(store: T): T {
    assertAndContinue(this.stores.includes(store), 'Tried to access store without having listened to it.');
    return store;
  }

  subscribe(onStoreChange: () => void): Unsubscribe {
    for (const store of this.stores) {
      store.addChangeListener(onStoreChange);
    }
    let unsubscribeRedux: Unsubscribe | null = null;
    if (this.shouldSubscribeToRedux) {
      unsubscribeRedux = getStore().subscribe(onStoreChange);
    }

    return () => {
      if (unsubscribeRedux) {
        unsubscribeRedux();
      }
      for (const store of this.stores) {
        store.removeChangeListener(onStoreChange);
      }
    };
  }
}
